import { Separator, Text } from '@cuemath/web-comps';
import React from 'react';
import styled from 'styled-components';
import {
  Button,
  Dropdown,
  Icon,
  Input,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
} from 'semantic-ui-react';
import SlotPage from './slot-page';
import { QUESTION_DATA } from './slot-page/utils';
import COLORS from '../../constants/colors';
import { MODAL_NAMES } from './utils';
import { Col, FlexView } from '@cuemath/comps';

// Step constants
const STEP_TIMEZONE = 1;
const STEP_SLOT_SELECTION = 2;

// Styled components
const TimezoneContainer = styled(Col)`
  min-height: 250px;
`;

const DropdownWrapper = styled.div`
  padding: 16px;
  background-color: ${COLORS.WILD_SAND};
  border-radius: 8px;
  border: 1px solid ${COLORS.IRON_LIGHT};
`;

const StyledDropdown = styled(Dropdown)`
  min-height: 40px;
  border: 1px solid ${COLORS.IRON_LIGHT} !important;
`;

const HelperText = styled(Text)`
  font-style: italic;
`;

const OtpContainer = styled(Col)`
  background: ${COLORS.WILD_SAND};
  border-radius: 8px;
`;

const getButtonLabel = type => {
  switch (type) {
    case MODAL_NAMES.assignSlot:
      return 'Assign Slot';
    case MODAL_NAMES.rescheduleSlot:
    case MODAL_NAMES.rescheduleSlotDifferentTutor:
    case MODAL_NAMES.rescheduleSlotSameTutor:
      return 'Confirm Reschedule';
    default:
      return 'Submit';
  }
};

const getHeading = (type, studentName) => {
  switch (type) {
    case MODAL_NAMES.assignSlot:
      return `Assign Slot`;
    case MODAL_NAMES.rescheduleSlot:
    case MODAL_NAMES.rescheduleSlotDifferentTutor:
    case MODAL_NAMES.rescheduleSlotSameTutor:
      return `Reschedule Trial`;
    default:
      return `Confirm Slot`;
  }
};

const getSubHeading = (type, studentName, opportunityRegion) => {
  switch (type) {
    case MODAL_NAMES.assignSlot:
      return `Assign an available slot to ${studentName} in ${opportunityRegion} region.`;
    case MODAL_NAMES.rescheduleSlot:
    case MODAL_NAMES.rescheduleSlotDifferentTutor:
    case MODAL_NAMES.rescheduleSlotSameTutor:
      return `Select Slot - Reschedule the trial for ${studentName}.`;
    default:
      return `Confirm Slot`;
  }
};

const AssignSlot = ({
  closeModal,
  studentName,
  timezone,
  opportunityRegion,
  onAssignSlots,
  bufferHours,
  type,
  setOtpState,
  otpState,
  progressStates,
  availableTimezones = [],
}) => {
  const [data, setData] = React.useState({});
  const [code, setCode] = React.useState('');
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [timer, setTimer] = React.useState(0);
  const [step, setStep] = React.useState(STEP_TIMEZONE);
  const [selectedTimezone, setSelectedTimezone] = React.useState(timezone || '');

  const updateStateData = slotInfo => {
    setData(slotInfo);
  };

  const handleAssignSlots = () => {
    onAssignSlots({ ...data, timezone: selectedTimezone }, 'schedule_demo');
    closeModal();
  };

  const sendOTP = () => {
    setOtpState({ state: 'initialize', isLoading: true });
    onAssignSlots({ ...data, timezone: selectedTimezone }, 'send_confirmation_code');
    setIsDisabled(true);
    setTimer(30);
  };

  const onVerification = () => {
    setOtpState({ state: 'sent', isLoading: true, isDisabled: true });
    onAssignSlots(
      { ...data, verificationCode: code, timezone: selectedTimezone },
      'verify_confirmation_code',
    );
  };

  const handleChange = (e, { value }) => {
    setCode(value);
  };

  const handleTimezoneChange = (e, { value }) => {
    setSelectedTimezone(value);
  };

  const handleNextStep = () => {
    setStep(STEP_SLOT_SELECTION);
  };

  const handleBackStep = () => {
    setStep(STEP_TIMEZONE);
  };

  React.useEffect(() => {
    let interval = null;

    if (isDisabled && timer > 0 && otpState?.state === 'sent') {
      interval = setInterval(() => {
        setTimer(timer => timer - 1);
      }, 1000);
    } else if (timer === 0 && isDisabled) {
      setIsDisabled(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isDisabled, otpState, otpState?.state, setOtpState, timer]);

  React.useEffect(() => {
    return () => {
      if (setOtpState) {
        setOtpState(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selectedTimezone with timezone prop changes
  React.useEffect(() => {
    setSelectedTimezone(timezone || '');
  }, [timezone]);

  const isButtonDisabled = !data?.time?.from;
  const btnLabel = getButtonLabel(type);
  const heading = getHeading(type, studentName);
  const subHeading = getSubHeading(type, studentName, opportunityRegion);
  const infoText = `Slots can be ${
    type === MODAL_NAMES.assignSlot ? 'assigned' : 'rescheduled'
  } at least ${
    bufferHours < 1 ? '30 minutes' : `${bufferHours} hours`
  } away from the current time.`;

  return (
    <Modal open={true} onClose={closeModal}>
      <ModalHeader>
        <Text variant="h6">{heading}</Text>
        <Text variant="body3" color={COLORS.GRAY}>
          {step === STEP_TIMEZONE
            ? 'Step 1: Confirm timezone for the trial slot'
            : subHeading}
        </Text>
        {step === STEP_SLOT_SELECTION && (
          <Segment>
            <Text variant="body2">
              <Icon name="info circle" /> {infoText}
            </Text>
          </Segment>
        )}
        {step === STEP_SLOT_SELECTION && type === MODAL_NAMES.rescheduleSlotSameTutor && (
          <Segment>
            <Text variant="body2">
              <Icon name="info circle" /> Requires tutor to share 4-digit code for
              acknowledgement.
            </Text>
          </Segment>
        )}
      </ModalHeader>
      <ModalContent scrolling>
        <ModalDescription>
          {step === STEP_TIMEZONE ? (
            <TimezoneContainer gutter={24}>
              <Text variant="body1Medium">Confirm Timezone</Text>
              <Separator height={12} />
              <DropdownWrapper>
                <StyledDropdown
                  fluid
                  selection
                  search
                  onChange={handleTimezoneChange}
                  options={availableTimezones}
                  value={selectedTimezone}
                  placeholder="Select a timezone"
                />
                <Separator height={8} />
                <HelperText variant="body3" color={COLORS.GRAY}>
                  {availableTimezones.length} timezones available - scroll to view all
                  options
                </HelperText>
              </DropdownWrapper>
              <Separator height={16} />
              <Text variant="body3" color={COLORS.GRAY}>
                Selected timezone will be used to display available slots for{' '}
                {studentName} in {opportunityRegion} region.
              </Text>
            </TimezoneContainer>
          ) : selectedTimezone ? (
            <SlotPage
              questionsData={QUESTION_DATA}
              timezone={selectedTimezone}
              opportunityRegion={opportunityRegion}
              updateStateData={updateStateData}
              bufferHours={bufferHours}
            />
          ) : (
            <Text variant="body2" color={COLORS.RUSTY_RED}>
              No timezone selected
            </Text>
          )}
          {step === STEP_SLOT_SELECTION &&
            type === MODAL_NAMES.rescheduleSlotSameTutor &&
            otpState?.state === 'sent' && (
              <>
                <OtpContainer gutter={16} gap={16}>
                  <Text variant="body1Medium">Enter 4-digit code from tutor:</Text>
                  <Separator height={16} />
                  <FlexView alignItems="center">
                    <Input
                      value={code}
                      onChange={handleChange}
                      type="text"
                      maxLength={4}
                    />
                    <Separator height={16} />
                    <Button color="black" onClick={sendOTP} disabled={isDisabled}>
                      {isDisabled ? `Resend in ${timer}s` : 'Resend'}
                    </Button>
                  </FlexView>
                </OtpContainer>
              </>
            )}
        </ModalDescription>
      </ModalContent>
      <ModalActions>
        {otpState?.error && (
          <Text variant="body2" color={COLORS.RED}>
            {otpState?.error}
          </Text>
        )}
        <Separator height={8} />
        <Button onClick={closeModal} basic>
          Cancel
        </Button>
        {step === STEP_TIMEZONE && (
          <Button onClick={handleNextStep} color="black" disabled={!selectedTimezone}>
            Continue
          </Button>
        )}
        {step === STEP_SLOT_SELECTION && (
          <>
            <Button onClick={handleBackStep} basic>
              Back
            </Button>
            {type === MODAL_NAMES.rescheduleSlotSameTutor &&
              (!otpState?.state || otpState?.state === 'initialize') && (
                <Button
                  onClick={sendOTP}
                  color="black"
                  disabled={isButtonDisabled}
                  loading={otpState?.isLoading}
                >
                  Send Code to Tutor
                </Button>
              )}
            {type !== MODAL_NAMES.rescheduleSlotSameTutor && (
              <Button
                onClick={handleAssignSlots}
                color="black"
                disabled={isButtonDisabled}
              >
                {btnLabel}
              </Button>
            )}
            {type === MODAL_NAMES.rescheduleSlotSameTutor && otpState?.state === 'sent' && (
              <Button
                onClick={onVerification}
                color="black"
                disabled={code.length < 4}
                loading={otpState?.isDisabled}
              >
                {btnLabel}
              </Button>
            )}
          </>
        )}
      </ModalActions>
    </Modal>
  );
};

export default AssignSlot;
